/**
 * Delivery Lines sub-tab (under Purchasing → ASN).
 *
 * Lists released delivery lines (sourced from the DNA ERP import →
 * `call_off.DeliveryLine`) with multi-select and per-row expandable detail.
 * The sender role can select one or more lines and click "Create ASN" to
 * build a single ASN from the selected lines via
 * `purchasingAPI.createASN({ delivery_line_ids })`. After a successful create
 * the parent is notified so it can switch to the ASN sub-tab and refresh it.
 *
 * The table and filter panel live in sibling files to keep this under the
 * 500-line limit.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Loader2, Truck, Plus, Filter, Download } from 'lucide-react';
import { purchasingAPI, coreAPI, customersAPI, DeliveryLine, CommodityGroup, CommodityType, CompanyGroup, CustomerProfile } from '../../../lib/api';
import { Pagination } from '../../ui/pagination';
import { useIsCustomer } from '../../../hooks/usePortalMode';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import {
  getDeliveryLineCustomerColumnLabel,
  getDeliveryLineCustomerGroupLabel,
  getDeliveryLineSupplierColumnLabel,
} from '../../../lib/portalLabels';
import { DeliveryLinesTable } from './DeliveryLinesTable';
import { DeliveryLinesFilter, DeliveryLinesFilters } from './DeliveryLinesFilter';
import { CreateASNFromLinesModal } from '../asn-wizard/CreateASNFromLinesModal';

const EMPTY_FILTERS: DeliveryLinesFilters = {
  poNumber: '',
  partNumber: '',
  supplier: '',
  customer: '',
  customerGroup: '',
  commodityGroup: '',
  commodityType: '',
  dateFrom: '',
  dateTo: '',
};

/**
 * Map the on-screen delivery-line filters onto the delivery-lines list/export
 * query params. ASN-only filters (courier, tracking, ASN number/status) have
 * no delivery-line analogue and are intentionally not represented here.
 */
function applyFilterParams(
  params: Record<string, string | number | boolean>,
  f: DeliveryLinesFilters,
): void {
  if (f.poNumber) params['po_order_number__icontains'] = f.poNumber;
  if (f.partNumber) params['part_number__icontains'] = f.partNumber;
  if (f.supplier) params['supplier'] = f.supplier;
  if (f.customer) params['customer'] = f.customer;
  if (f.customerGroup) params['customer__group'] = f.customerGroup;
  if (f.commodityGroup) params['po_line__commodity_group'] = f.commodityGroup;
  if (f.commodityType) params['po_line__commodity_type'] = f.commodityType;
  if (f.dateFrom) params['date_released__gte'] = f.dateFrom;
  if (f.dateTo) params['date_released__lte'] = f.dateTo;
}

interface DeliveryLinesTabProps {
  /** Called after a successful ASN create so the parent can switch tabs + refresh. */
  onASNCreated?: () => void;
}

/**
 * Released PO lines list with selection + Create-ASN flow.
 *
 * @param onASNCreated - invoked after a successful create (parent switches to ASN tab)
 */
export default function DeliveryLinesTab({ onASNCreated }: DeliveryLinesTabProps) {
  const { isSupplierPortalMode } = useMenuSettings();
  const { formatDate: ctxFormatDate, formatCurrency } = useFormatters();

  // Create-ASN is gated to the sender role, exactly as the ASN list tab.
  const isCustomer = useIsCustomer();
  // ASNs are created by suppliers only (Poeton ships to its customers).
  const canCreateASN = !isCustomer;
  const supplierLabel = getDeliveryLineSupplierColumnLabel(isSupplierPortalMode);
  const customerLabel = getDeliveryLineCustomerColumnLabel();
  const customerGroupLabel = getDeliveryLineCustomerGroupLabel();

  const [lines, setLines] = useState<DeliveryLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  // Track the full selected line objects (not just ids) so we can validate a
  // single-counterparty selection even when it spans multiple pages.
  const [selectedLines, setSelectedLines] = useState<Map<number, DeliveryLine>>(new Map());
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const selectedIds = useMemo(() => new Set(selectedLines.keys()), [selectedLines]);

  // ASN counterparty is always the customer (Poeton is the supplier; it
  // ships to its customers). No mode switching.
  const counterpartyOf = useCallback((l: DeliveryLine) => l.customer, []);
  const isMixedSupplier = useMemo(
    () => new Set(Array.from(selectedLines.values()).map(counterpartyOf)).size > 1,
    [selectedLines, counterpartyOf],
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState<DeliveryLinesFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<DeliveryLinesFilters>(EMPTY_FILTERS);
  const [supplierOptions, setSupplierOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerGroupOptions, setCustomerGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load filter dropdown options once (mirrors ASNListTab's option loading).
  useEffect(() => {
    Promise.all([
      purchasingAPI.getLineFilterOptions().catch(() => ({ data: { suppliers: [], customers: [], commodity_groups: [], commodity_types: [] } })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      coreAPI.getCustomerGroupsForFilter().catch(() => ({ data: { results: [] } })),
      customersAPI.getProfiles({ page_size: 1000, is_active: true }).catch(() => ({ data: { results: [] } })),
    ]).then(([filterOptsRes, groupsRes, typesRes, companyGroupsRes, customersRes]) => {
      const suppliers = filterOptsRes.data.suppliers || [];
      setSupplierOptions(suppliers.map((c) => ({ value: String(c.id), label: c.name })));

      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      setCommodityGroupOptions(groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name })));

      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      setCommodityTypeOptions(types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name })));

      const customers = (Array.isArray(customersRes.data) ? customersRes.data : (customersRes.data as unknown as { results: CustomerProfile[] }).results || []) as CustomerProfile[];
      setCustomerOptions(customers.map((s) => ({ value: String(s.company), label: s.company_name })));

      const companyGroups = (companyGroupsRes.data as unknown as { results: CompanyGroup[] }).results || [];
      setCustomerGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));
    });
  }, []);

  const fetchLines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string | number | boolean> = {
        // Only lines not yet placed on an ASN.
        available: true,
        ordering: '-date_released',
        page: currentPage,
        page_size: pageSize,
      };
      applyFilterParams(params, appliedFilters);

      const response = await purchasingAPI.getDeliveryLines(params);
      setLines(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (err: unknown) {
      console.error('Error fetching delivery lines:', err);
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load delivery lines');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    fetchLines();
  }, [fetchLines]);

  // Debounce text filters into appliedFilters.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters]);

  const handleFilterChange = (key: keyof DeliveryLinesFilters, value: string) => {
    setFilters((prev) => {
      // Changing the commodity group clears any selected commodity type, since
      // types are scoped to a group (mirrors the ASN filter behaviour).
      if (key === 'commodityGroup') {
        return { ...prev, commodityGroup: value, commodityType: '' };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  const handleToggleSelect = (line: DeliveryLine) => {
    setSelectedLines((prev) => {
      const next = new Map(prev);
      if (next.has(line.id)) next.delete(line.id);
      else next.set(line.id, line);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedLines((prev) => {
      const allSelected = lines.length > 0 && lines.every((l) => prev.has(l.id));
      const next = new Map(prev);
      if (allSelected) lines.forEach((l) => next.delete(l.id));
      else lines.forEach((l) => next.set(l.id, l));
      return next;
    });
  };

  const handleToggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Export the filtered delivery lines (same scope/filters as the table) to
  // CSV or Excel via the backend export action.
  const handleExport = async (fileFormat: 'csv' | 'xlsx') => {
    try {
      setExporting(true);
      const params: Record<string, string | number | boolean> = { available: true, ordering: '-date_released', file_format: fileFormat };
      applyFilterParams(params, appliedFilters);
      const resp = await purchasingAPI.exportDeliveryLines(params);
      const blob = new Blob([resp.data], {
        type: fileFormat === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `delivery_lines_${new Date().toISOString().split('T')[0]}.${fileFormat}`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Delivery lines export completed');
    } catch (err: unknown) {
      console.error('Delivery lines export failed:', err);
      toast.error('Failed to export delivery lines');
    } finally {
      setExporting(false);
    }
  };

  const handleASNCreated = () => {
    toast.success('ASN created successfully');
    setSelectedLines(new Map());
    onASNCreated?.();
  };

  const formatDate = (dateString?: string | null) => (dateString ? ctxFormatDate(dateString) : '-');

  if (loading && lines.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading delivery lines...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button onClick={fetchLines} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <DeliveryLinesFilter
          filters={filters}
          supplierOptions={supplierOptions}
          customerOptions={customerOptions}
          customerGroupOptions={customerGroupOptions}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          supplierLabel={supplierLabel}
          customerLabel={customerLabel}
          customerGroupLabel={customerGroupLabel}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          onSearch={() => {
            setAppliedFilters(filters);
            setCurrentPage(1);
          }}
        />
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Delivery Lines</h3>
              <span className="text-sm text-muted-foreground">
                {totalCount} {totalCount === 1 ? 'line' : 'lines'}
                {selectedIds.size > 0 ? ` • ${selectedIds.size} selected` : ''}
              </span>
              {isMixedSupplier && (
                <span className="text-xs font-medium text-amber-600">
                  Selected lines span multiple {customerLabel}s — an ASN must be for a single {customerLabel}.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-1 ${
                  showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filter Deliveries
              </button>
              <button
                onClick={() => handleExport('csv')}
                disabled={exporting || lines.length === 0}
                className="px-3 py-1.5 text-sm rounded-lg border flex items-center gap-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export filtered delivery lines to CSV"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                CSV
              </button>
              <button
                onClick={() => handleExport('xlsx')}
                disabled={exporting || lines.length === 0}
                className="px-3 py-1.5 text-sm rounded-lg border flex items-center gap-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export filtered delivery lines to Excel"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Excel
              </button>
              {canCreateASN && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  disabled={selectedIds.size === 0 || isMixedSupplier}
                  title={isMixedSupplier ? 'All selected lines must be for the same Supplier' : undefined}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-tour="create-asn-from-lines-btn"
                >
                  <Plus className="h-4 w-4" />
                  Create ASN
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {lines.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No released delivery lines found.
            </div>
          ) : (
            <>
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="lines"
                />
              </div>

              <DeliveryLinesTable
                lines={lines}
                selectedIds={selectedIds}
                expandedId={expandedId}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onToggleExpand={handleToggleExpand}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                showCustomerAsSupplier={true}
                counterpartyColumnLabel={customerLabel}
              />

              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="lines"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      <CreateASNFromLinesModal
        isOpen={showCreateModal}
        deliveryLineIds={Array.from(selectedIds)}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleASNCreated}
      />
    </div>
  );
}
